import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { classNames } from '@progress/kendo-react-common';
import { Button, Chip } from '@progress/kendo-react-buttons';

import { FilterBuilder } from './FilterBuilder';
import { KWELLO_ICON } from '../../../components/icon/KwelloSvgIcon';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import {
  applyCommonFilter,
  clearFilters,
  rollbackFilterChanges,
  selectCommonFilters,
  updateCommonFilters,
} from '../../../store/slices/common-filters.slice';
import {
  applyDateFilter,
  resetDateFilters,
  rollbackDateFilter,
  selectTempDateFilter,
} from '../../../store/slices/date-filter.slice';
import { toggleFilters } from '../../../store/slices/filter-builder.slice';
import { DatasetID, FilterParent } from '../../../models/filter.model';
import { RootState } from '../../../store';
import { useGetUserDataListQuery } from '../../../store/api/user-data.api';
import {
  selectSavedFilterKey,
  selectSavedFiltersEntries,
  setSavedFilterKey,
  setSavedFiltersEntries,
} from '../../../store/slices/saved-filters.slice';
import {
  selectDeleteFilter,
  selectSaveCriteriaVisible,
  showSaveCriteria,
} from '../../../store/slices/save-criteria-modals.slice';
import { SaveCriteriaModal } from '../../save-criteria/SaveCriteriaModal';
import { SavedCriteriaFilterModel } from '../../../models/saved-filters.model';
import SavedCriteria from './SavedCriteria';
import { DeleteCriteriaModal } from '../../../components/delete-criteria/DeleteCriteriaModal';
import { SubscribeCriteriaModal } from '../../../components/subscribe-criteria/SubscribeCriteriaModal';
import { clearChartFilters, selectChartFilters } from '../../../store/slices/chart-filters.slice';
import { FlyoutBase, FlyoutBody, FlyoutFooter } from '../../flyouts/Flyout';
import useOutsideClick from 'common/hooks/useOutsideClick';
import useFilterValidation from 'common/hooks/useFilterValidation';
import { FlyoutHeader } from 'common/components/flyouts/components/FlyoutHeader';
import { UserDataType } from '../../../models/user.model';
import {
  selectOverrideDateFilter,
  updateUniversalFilters,
} from 'common/store/slices/dashboard-editor.slice';
interface MergedFilterValue {
  name: string;
  value: string | number;
  sortOrder?: number;
}

interface MergedFilter {
  type: number;
  label: string;
  value: MergedFilterValue[];
}

type MergedFiltersMap = Record<string, MergedFilter>;
interface FilterFlyoutProps extends DatasetID {
  titleOverride?: string;
  subTitleOverride?: string;
  iconOverride?: KWELLO_ICON;
  hideSaveCriteria?: boolean;
}

export default function FilterFlyout({
  datasetId,
  titleOverride,
  subTitleOverride,
  iconOverride,
  hideSaveCriteria,
}: Readonly<FilterFlyoutProps>) {
  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState(0);

  const { data, refetch } = useGetUserDataListQuery({ type: UserDataType.SavedCriteria });
  const chartFilters = useAppSelector((state: RootState) => selectChartFilters(state, datasetId));
  const saveCriteriaDialogVisible = useAppSelector(selectSaveCriteriaVisible);
  const deleteFilter = useAppSelector(selectDeleteFilter);
  const filters = useAppSelector((state: RootState) => selectCommonFilters(state, datasetId));
  const savedFilters = useAppSelector(selectSavedFiltersEntries);
  const selectedSavedFilter = useAppSelector((state: RootState) =>
    selectSavedFilterKey(state, datasetId)
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const filterFormValid = useFilterValidation({ datasetId, validateAllFilters: true });
  const tempDateFilters = useAppSelector((state: RootState) =>
    selectTempDateFilter(state, datasetId)
  );
  const overrideDateFilter = useAppSelector(selectOverrideDateFilter);

  const setOpen = useCallback(
    (next: boolean) => {
      dispatch(toggleFilters({ visible: next, datasetId }));
    },
    [dispatch, datasetId]
  );

  const closeFilters = useCallback(() => {
    setOpen(false);
    dispatch(rollbackFilterChanges({ datasetId }));
    dispatch(rollbackDateFilter({ datasetId }));
  }, [setOpen, dispatch, datasetId]);

  useOutsideClick(containerRef, closeFilters);

  useEffect(() => {
    if (data) {
      const filterEntries = data.reduce<{ key: number; value: SavedCriteriaFilterModel }[]>(
        (acc, item) => {
          const value = item.value;
          if (typeof value === 'string') {
            const parsedItem: SavedCriteriaFilterModel = JSON.parse(value);

            if (parsedItem.filterParent) {
              acc.push({ key: item.key, value: parsedItem });
            }
          } else if ('filterParent' in value) {
            acc.push({ key: item.key, value });
          }

          return acc;
        },
        []
      );

      dispatch(setSavedFiltersEntries({ entries: filterEntries }));
    }
  }, [data, dispatch]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const applyFilters = useCallback(() => {
    setOpen(false);
    const hasSankeyFilters = chartFilters?.some((f) => f.id === 'alignment_movement');
    const mergedFilters: MergedFiltersMap = hasSankeyFilters
      ? (JSON.parse(JSON.stringify(filters)) as MergedFiltersMap)
      : (filters as MergedFiltersMap);

    const alignmentFilterPayload: Record<string, number[]> = {};

    if (hasSankeyFilters) {
      const allowedKeys = ['PreviousAlignmentScoreLabel', 'AlignmentScoreLabel'];

      chartFilters
        .filter((cf) => allowedKeys.includes(cf.propertyName))
        .forEach((cf) => {
          const key = cf.propertyName;
          const valueObj: MergedFilterValue = {
            name: cf.propertyDisplayValue ?? cf.value,
            value: cf.value,
            sortOrder: 0,
          };

          if (mergedFilters[key]) {
            const existingValues = mergedFilters[key].value.map((v) => v.value.toString());
            if (!existingValues.includes(cf.value.toString())) {
              mergedFilters[key].value.push(valueObj);
            }
          } else {
            mergedFilters[key] = {
              type: 1,
              label: cf.propertyDisplayName ?? key,
              value: [valueObj],
            };
          }
        });

      Object.keys(mergedFilters).forEach((key) => {
        if (allowedKeys.includes(key)) {
          mergedFilters[key].value = mergedFilters[key].value.map((v) => {
            const id = getAlignmentLabelId(v.value.toString());
            return { ...v, value: id ?? v.value };
          });
        }
      });

      allowedKeys.forEach((key) => {
        if (mergedFilters[key]?.value) {
          const uniqueMap = new Map<string | number, MergedFilterValue>();
          mergedFilters[key].value.forEach((v) => {
            const normalized = Number(v.value) || v.value;
            if (!uniqueMap.has(normalized)) {
              uniqueMap.set(normalized, v);
            }
          });
          mergedFilters[key].value = Array.from(uniqueMap.values());
        }
      });

      chartFilters
        .filter((f) => allowedKeys.includes(f.propertyName))
        .forEach((filter) => {
          const idValue = getAlignmentLabelId(filter.value.toString());

          if (!idValue) return;

          const targetKey =
            filter.propertyName === 'PreviousAlignmentScoreLabel'
              ? 'PreviousAlignmentScoreLabelIds'
              : 'AlignmentScoreLabelIds';

          alignmentFilterPayload[targetKey] = [
            ...(alignmentFilterPayload[targetKey] || []),
            idValue,
          ];
        });
    }

    dispatch(
      updateCommonFilters({
        datasetId,
        filters: Object.entries(mergedFilters).map(([key, filter]) => ({
          key,
          type: filter.type ?? 1,
          label: filter.label ?? key,
          filter,
        })),
      })
    );

    dispatch(applyCommonFilter({ datasetId }));
    dispatch(applyDateFilter({ datasetId }));
    dispatch(setSavedFilterKey({ datasetId, value: undefined }));

    if (datasetId.includes('universal-')) {
      // If it's a universal filter, update the dashboard.
      dispatch(
        updateUniversalFilters({
          filterParent: FilterParent.UNIVERSAL,
          filters: mergedFilters,
          startDate: tempDateFilters.startDate,
          endDate: tempDateFilters.endDate,
          visualDataType: tempDateFilters.visualType,
          visualData: tempDateFilters.visualData,
          overrideDateFilter,
        })
      );
    }
  }, [setOpen, dispatch, datasetId, filters, chartFilters, tempDateFilters, overrideDateFilter]);

  const onSaveCriteria = useCallback(() => {
    dispatch(showSaveCriteria());
  }, [dispatch]);

  const onClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const appliedSavedFilter = useMemo(() => {
    return savedFilters?.find((filter) => filter.key === selectedSavedFilter);
  }, [savedFilters, selectedSavedFilter]);

  return (
    <>
      <FlyoutBase onOverlayClick={closeFilters}>
        {(saveCriteriaDialogVisible || !!deleteFilter) && <div className="flyout-overlay" />}
        <FlyoutHeader
          title={titleOverride ?? 'Filters'}
          subTitle={subTitleOverride}
          onCloseClick={closeFilters}
          icon={iconOverride ?? KWELLO_ICON.FILTER_ICON}
        />
        <FlyoutBody>
          {!hideSaveCriteria && (
            <div className="flyout-panel-tabs">
              <Button
                fillMode="flat"
                className={classNames({ ['flyout-panel-active-tab']: activeTab === 0 })}
                size={'large'}
                onClick={() => setActiveTab(0)}
                rounded={null}
              >
                Filters
              </Button>
              <Button
                fillMode="flat"
                disabled={!savedFilters?.length}
                size={'large'}
                className={classNames({ ['flyout-panel-active-tab']: activeTab === 1 })}
                onClick={() => setActiveTab(1)}
                rounded={null}
              >
                Saved Criteria
              </Button>
            </div>
          )}
          {appliedSavedFilter && activeTab === 0 && (
            <div className="flyout-panel-applied-filter">
              <Chip
                removable
                fillMode={'outline'}
                onRemove={(e) => {
                  e.syntheticEvent.stopPropagation();
                  e.syntheticEvent.preventDefault();
                  dispatch(setSavedFilterKey({ datasetId, value: undefined }));
                  dispatch(clearFilters({ datasetId }));
                  dispatch(clearChartFilters({ datasetId }));
                  dispatch(resetDateFilters({ datasetId }));
                }}
              >
                <span className="k-chip-label">Saved Search&nbsp;</span>
                <b className={'color-primary'}>{appliedSavedFilter.value.name}</b>
              </Chip>
            </div>
          )}
          {activeTab === 0 && <FilterBuilder datasetId={datasetId} />}
          {activeTab === 1 && <SavedCriteria datasetId={datasetId} onClose={onClose} />}
        </FlyoutBody>
        <FlyoutFooter>
          {activeTab === 0 && (
            <>
              {!hideSaveCriteria && (
                <Button
                  themeColor="secondary"
                  disabled={!Object.keys(filters).length || !filterFormValid}
                  color="primary"
                  fillMode="outline"
                  className=""
                  onClick={onSaveCriteria}
                >
                  Save Criteria
                </Button>
              )}
              <Button
                onClick={applyFilters}
                fillMode="solid"
                themeColor="primary"
                disabled={!filterFormValid}
              >
                Apply
              </Button>
            </>
          )}
        </FlyoutFooter>
      </FlyoutBase>
      <SaveCriteriaModal datasetId={datasetId} onClose={onClose} />
      <DeleteCriteriaModal />
      <SubscribeCriteriaModal />
    </>
  );
}

// Utility function to dynamically map alignment labels to IDs
const getAlignmentLabelId = (label: string): number | undefined => {
  const ALIGNMENT_LABELS_MAP: Record<string, number> = {
    'Not Aligned': 1,
    Minimally: 2,
    Neutral: 3,
    Mostly: 4,
    Fully: 5,
  };
  const normalized = label.toLowerCase().trim();
  const foundKey = Object.keys(ALIGNMENT_LABELS_MAP).find(
    (key) => key.toLowerCase() === normalized
  );
  return foundKey ? ALIGNMENT_LABELS_MAP[foundKey] : undefined;
};
